import {type ReactNode, useMemo} from 'react'
import {
  ScrollView,
  type StyleProp,
  StyleSheet,
  Text,
  type TextStyle,
  TouchableOpacity,
  View,
} from 'react-native'
import Svg, {Circle} from 'react-native-svg'
import {Feather} from '@expo/vector-icons'
import {Plural, Trans, useLingui} from '@lingui/react/macro'

import {
  directionTextStyle,
  type FlashBookDirection,
} from '#/lib/flashbookDirection'
import {buildFlashBookPageTrouble} from '#/lib/flashbookPageTrouble'
import {type FlashBookReadProgress} from '#/lib/flashbookProgress'
import {buildFlashBookReadHistory} from '#/lib/flashbookReadHistory'
import {
  buildFlashBookReport,
  type FocusEntry,
  type PageRow,
  type ReportInsight,
  type SkillRow,
} from '#/lib/flashbookReport'
import {
  FlashBookPlayThemeProvider,
  useFlashBookPlayDesign,
  useFlashBookPlayPalette,
  usePlayPrimaryAction,
  usePlayStackGap,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {FlashBookReadHistorySection} from '#/components/flashbooks/FlashBookReadHistorySection'
import {FlashBookTroubledPagesSection} from '#/components/flashbooks/FlashBookTroubledPagesSection'
import {
  formatDuration,
  useCopyStyle,
  useReportType,
  useReportVerdict,
} from '#/components/flashbooks/useReportChrome'
import {type FlashBookDesign} from '#/themes-flashbook/design'
import {
  type FlashBookPage,
  type FlashBookPageResult,
  type FlashBookThemeTokens,
} from '#/types/flashbook'

/**
 * The last page of a book: what the read added up to.
 *
 * This is the one screen a learner meets *after* the work, so it answers three
 * questions in that order — how did I do, where am I strong and weak, and what
 * exactly should I look at again. Which is why it is not a list of every block in
 * the book any more: a hundred pages of results is a log, and a log is not a
 * report. The facts come from `lib/flashbookReport`; everything here is
 * presentation, so the arithmetic can be tested without rendering anything and the
 * wording can be translated without touching the arithmetic.
 *
 * It is printed **on the book's own paper**, in the book's own type, inside the
 * theme provider the rest of the reader uses — a report that arrived in the app's
 * UI font on a grey panel would look like a different application's screen at the
 * end of a handwritten workbook.
 */
type Props = {
  bookTitle: string
  pages: FlashBookPage[]
  pageResults: FlashBookPageResult[]
  /** Wall-clock length of the read. */
  elapsedMs: number
  themeTokens: FlashBookThemeTokens
  /** The book's design language, so the report is set like the book. */
  design?: FlashBookDesign
  /** The direction the book is written in — the report is page furniture. */
  direction?: FlashBookDirection
  /**
   * Which read of this book this was, counting this one.
   *
   * A book read over days is read more than once, and a report that never says so
   * makes every read look like the first: the same verdict, the same numbers, no
   * sign that last week's was worse — which is the one comparison that gives a
   * number meaning. Omitted when the read was not registered (a preview), and not
   * shown at all for a first read, where "this is your first read" is not news.
   */
  readNumber?: number
  /**
   * How many practices have been sat on this book.
   *
   * A practice is not a read (see `#/lib/flashbookProgress`), so it gets its own
   * line rather than inflating the read count — a learner who has practised eight
   * times has read the book once, and telling them otherwise would be the app
   * taking credit for their work.
   */
  practiceCount?: number
  /**
   * The book's reading register, for the progression across reads.
   *
   * The counts above say *how many* times the book has been read; this is what
   * every one of them scored, so the report can show whether the learner is
   * getting better instead of only how they did today (see
   * `#/lib/flashbookReadHistory`). The register rather than a finished progression,
   * so the one place that knows what a read is stays the one place that decides it.
   * Absent for a preview, and for a fixture that has no history to show.
   */
  readProgress?: FlashBookReadProgress | null
  onReplay?: () => void
  /** Turn back to a page the learner should look at again. */
  onReviewPage?: (pageIndex: number) => void
  /**
   * Sit the questions worth another look as a practice of just those.
   *
   * The list below is the useful half of the report: *these eight*. Reading the
   * hundred-page book again to reach them is the useless half, so the report hands
   * the list to whoever can ask them once more (see `#/lib/flashbookReRead`).
   */
  onPractise?: (entries: FocusEntry[]) => void
  /**
   * Share this read — as a card in the feed, or as a picture to keep.
   *
   * Absent for a preview, where there is no read worth telling anybody about and
   * no session to tell it with.
   */
  onShare?: () => void
  /**
   * Open the class's own report.
   *
   * Only the book's author is offered this, because only they have a class to
   * report on: the same button on every learner's report would promise a teacher's
   * view of a book they cannot see anybody else's answers to. Absent for everybody
   * else, which is also how a preview and every fixture stay unchanged.
   */
  onOpenClassReport?: () => void
  onDone: () => void
  footer?: ReactNode
}

export function FlashBookReport({
  themeTokens,
  design,
  direction,
  ...props
}: Props) {
  return (
    <FlashBookPlayThemeProvider
      tokens={themeTokens}
      design={design}
      direction={direction}>
      <ReportBody {...props} tok={themeTokens} direction={direction} />
    </FlashBookPlayThemeProvider>
  )
}

function ReportBody({
  bookTitle,
  pages,
  pageResults,
  elapsedMs,
  readNumber,
  practiceCount,
  readProgress,
  tok,
  direction,
  onReplay,
  onReviewPage,
  onPractise,
  onShare,
  onOpenClassReport,
  onDone,
  footer,
}: Omit<Props, 'themeTokens' | 'design'> & {
  tok: FlashBookThemeTokens
}) {
  const {t: l} = useLingui()
  const type = useReportType()
  const copy = useCopyStyle()
  const pal = useFlashBookPlayPalette()
  const pagePad = useFlashBookPlayDesign().page.padding
  const stackGap = usePlayStackGap(18)
  const playAction = usePlayPrimaryAction(48)
  // The book's own title is the book's writing, not the report's: it is set the way
  // the cover sets it even when the report around it is another language.
  const titleDir = directionTextStyle(direction ?? 'ltr')

  const model = useMemo(
    () => buildFlashBookReport({pages, pageResults, elapsedMs}),
    [pages, pageResults, elapsedMs],
  )

  /*
   * The learner's history of this book, or `null` when there is nothing to show.
   *
   * Built from the register rather than passed finished, so the same module that
   * decides what a scoreless read is decides it here too — and memoised because
   * the report re-renders on every scroll of the pager behind it.
   */
  const history = useMemo(
    () => buildFlashBookReadHistory(readProgress),
    [readProgress],
  )

  /*
   * The pages that have cost the learner a question in more than one read.
   *
   * The other half of "across reads": where the progression says how the *scores*
   * compare, this says which page keeps doing the damage. Built from the register
   * and the book as it is now, because a row's whole action is turning back to the
   * page — and it needs `pages` for that, which the history does not.
   */
  const trouble = useMemo(
    () => buildFlashBookPageTrouble({progress: readProgress, pages}),
    [pages, readProgress],
  )

  const {counts, score} = model
  const graded = counts.exercises > 0
  const title = {color: pal.text, textAlign: 'center'} as const

  /*
   * The headline — a band, a claim, and what to do next — from the one place that
   * has it: the shared card prints the same verdict, and a learner who reads
   * "Strong read" here must not see different words on the picture they post.
   */
  const verdict = useReportVerdict(model)

  return (
    <View
      testID="flashbook-report"
      style={[
        styles.root,
        // The book *is* the paper: the report is its last page, so it prints on
        // the page's own surface rather than on the shell behind the book.
        {backgroundColor: tok.pageBg},
        copy.row,
      ]}>
      <ScrollView
        contentContainerStyle={[
          styles.scroll,
          {paddingHorizontal: pagePad, gap: stackGap},
        ]}
        showsVerticalScrollIndicator={false}>
        {/* ── Verdict ─────────────────────────────────────────────────────── */}
        <View style={styles.hero}>
          <Text
            // The book's own title, so it is set the way the book writes — even when
            // the report around it is in another language.
            style={[type.caption, titleDir, {color: pal.muted}, title]}
            numberOfLines={2}>
            {bookTitle}
          </Text>

          <ScoreRing
            pct={score.pct}
            graded={graded}
            accent={tok.accent}
            track={pal.paperTrack}
            ink={pal.text}
            muted={pal.muted}
          />

          <Text
            testID="flashbook-report-verdict"
            style={[type.display, copy.text, {color: pal.text}, title]}>
            {verdict.title}
          </Text>
          <Text style={[type.body, copy.text, {color: pal.body}, title]}>
            {verdict.subtitle}
          </Text>

          {/*
            Where this read sits among the others.

            Two counts, kept apart: a read of the book, and a practice of the
            questions it named. A learner who opened the book once and practised
            the eight they missed has read it once — telling them otherwise is the
            report taking credit for work they did on top of the read. Each line
            appears only when it says something: a first read is not news, and
            neither is a book nobody has practised.
          */}
          {readNumber != null && readNumber > 1 && (
            <View
              testID="flashbook-report-reads"
              style={styles.readCount}
              accessibilityRole="text">
              <Feather
                name="repeat"
                size={12}
                color={pal.muted}
                style={styles.readCountIcon}
              />
              <Text style={[type.caption, copy.text, {color: pal.muted}]}>
                <Plural
                  value={readNumber}
                  one="read of this book"
                  other="# reads of this book"
                />
              </Text>
            </View>
          )}
          {practiceCount != null && practiceCount > 0 && (
            <View
              testID="flashbook-report-practices"
              style={styles.readCount}
              accessibilityRole="text">
              <Feather
                name="target"
                size={12}
                color={pal.muted}
                style={styles.readCountIcon}
              />
              <Text style={[type.caption, copy.text, {color: pal.muted}]}>
                <Plural
                  value={practiceCount}
                  one="practice sat on the questions below"
                  other="# practices sat on the questions below"
                />
              </Text>
            </View>
          )}
        </View>

        {/* ── The numbers ─────────────────────────────────────────────────── */}
        <View style={styles.tiles}>
          {graded && (
            <StatTile
              testID="flashbook-report-stat--points"
              icon="award"
              value={`${score.points}/${score.max}`}
              label={l`Points`}
            />
          )}
          <StatTile
            testID="flashbook-report-stat--correct"
            icon="check-circle"
            value={
              graded
                ? `${counts.correct}/${counts.exercises}`
                : `${counts.readingsHeard}/${counts.readings}`
            }
            label={graded ? l`Correct` : l`Readings`}
            tone={graded ? pal.positive : undefined}
          />
          <StatTile
            testID="flashbook-report-stat--time"
            icon="clock"
            value={formatDuration(model.time.totalMs)}
            label={l`Time`}
          />
          {graded && (
            <StatTile
              testID="flashbook-report-stat--pace"
              icon="zap"
              value={
                model.time.msPerExercise > 0
                  ? `${Math.round(model.time.msPerExercise / 1000)}s`
                  : '—'
              }
              label={l`Per question`}
            />
          )}
          {model.streak > 1 && (
            <StatTile
              testID="flashbook-report-stat--streak"
              icon="trending-up"
              value={`${model.streak}`}
              label={l`Best run`}
              tone={pal.positive}
            />
          )}
          {counts.skipped > 0 && (
            <StatTile
              testID="flashbook-report-stat--skipped"
              icon="help-circle"
              value={`${counts.skipped}`}
              label={l`Not answered`}
              tone={pal.warning}
            />
          )}
        </View>

        {/* ── What it means ───────────────────────────────────────────────── */}
        {model.insights.length > 0 && (
          <View style={styles.insights}>
            {model.insights.map(insight => (
              <InsightRow
                key={insight.kind}
                insight={insight}
                positive={pal.positive}
                warning={pal.warning}
                accent={tok.accent}
              />
            ))}
          </View>
        )}

        {/*
          ── How the reads compare ────────────────────────────────────────

          Sits above the page-by-page detail on purpose: for a learner opening this
          report for the second or third time, "better than last time?" is the
          question they came back for, and it should be answered before the body of
          *this* read rather than after it.
        */}
        {history && <FlashBookReadHistorySection history={history} />}

        {/*
          ── The pages that keep beating them ──────────────────────────────

          Below the progression and above the page-by-page body, which is exactly
          what it is: the history says whether the readings are getting better, this
          says where they keep going wrong, and the body below is this reading's
          detail. Shown only once a page has cost something *twice* — a page named
          for one bad answer is what the section above already listed (see
          `#/lib/flashbookPageTrouble`).
        */}
        {trouble.reads >= 2 && (
          <FlashBookTroubledPagesSection
            trouble={trouble}
            onReviewPage={onReviewPage}
          />
        )}

        {/* ── By skill ────────────────────────────────────────────────────── */}
        {graded && (
          <View style={styles.section}>
            <SectionTitleText>
              <Trans>How you did, by exercise</Trans>
            </SectionTitleText>
            {model.skills.map(skill => (
              <SkillBar key={skill.type} skill={skill} accent={tok.accent} />
            ))}
          </View>
        )}

        {/* ── Worth another look ──────────────────────────────────────────── */}
        <View style={styles.section}>
          <SectionTitleText>
            <Trans>Worth another look</Trans>
          </SectionTitleText>
          {/*
            *The* action of this section, and the reason the list is worth
            reading: these exact questions, once more, and what changed.

            It sits above the list rather than below it because it is what the
            list is *for* — a learner who reads eight questions and then finds a
            button has already scrolled past the thing they wanted. It is offered
            only when there is something to practise and somebody to ask.
          */}
          {model.focus.length > 0 && onPractise && (
            <TouchableOpacity
              testID="flashbook-report-practice-btn"
              accessibilityRole="button"
              accessibilityLabel={l`Practise these questions`}
              accessibilityHint={l`Asks only the questions worth another look, then compares`}
              onPress={() => onPractise(model.focus)}
              // The book's own action language, exactly as the section's `Done`
              // uses it: a practice is an action of the book, and on ruled paper
              // that means the underline and ink a workbook prints, not a pill.
              style={[
                styles.practiceBtn,
                {backgroundColor: tok.accent},
                playAction.style,
              ]}>
              <Feather
                name="repeat"
                size={16}
                color={playAction.ink(true) ?? pal.onAccent}
              />
              <View style={styles.practiceBtnText}>
                <Text
                  style={[
                    type.label,
                    copy.text,
                    {color: playAction.ink(true) ?? pal.onAccent},
                    playAction.labelStyle(true),
                  ]}>
                  <Plural
                    value={model.focus.length}
                    one="Practise this question"
                    other="Practise these # questions"
                  />
                </Text>
                <Text
                  style={[
                    type.caption,
                    copy.text,
                    {color: playAction.ink(true) ?? pal.onAccent},
                    playAction.labelStyle(true),
                  ]}>
                  <Trans>Just these, then see what changed.</Trans>
                </Text>
              </View>
              <Feather
                name="arrow-right"
                size={16}
                color={playAction.ink(true) ?? pal.onAccent}
              />
            </TouchableOpacity>
          )}
          {model.focus.length === 0 ? (
            <View
              testID="flashbook-report-focus--none"
              style={[
                styles.empty,
                {
                  borderColor: pal.paperHairline,
                  backgroundColor: pal.surfaceAlt,
                },
              ]}>
              <Feather name="check-circle" size={18} color={pal.positive} />
              <Text
                style={[type.body, copy.text, styles.flex, {color: pal.body}]}>
                {graded ? (
                  <Trans>
                    Nothing to redo — every question was answered correctly.
                  </Trans>
                ) : (
                  <Trans>No exercises in this book. Enjoy the read.</Trans>
                )}
              </Text>
            </View>
          ) : (
            model.focus.map(entry => (
              <FocusCard
                key={`${entry.pageIndex}-${entry.blockId}`}
                entry={entry}
                totalPages={pages.length}
                positive={pal.positive}
                negative={pal.negative}
                warning={pal.warning}
                accent={tok.accent}
                onOpen={
                  onReviewPage ? () => onReviewPage(entry.pageIndex) : undefined
                }
              />
            ))
          )}
        </View>

        {/* ── The journey, page by page ───────────────────────────────────── */}
        {model.pages.length > 0 && (
          <View style={styles.section}>
            <SectionTitleText>
              <Trans>Your journey</Trans>
            </SectionTitleText>
            {model.pages.length > 1 && (
              <MasteryStrip
                pages={model.pages}
                accent={tok.accent}
                onOpen={onReviewPage}
              />
            )}
            {model.pages.map(row => (
              <JourneyRow
                key={row.index}
                row={row}
                accent={tok.accent}
                onOpen={onReviewPage && (() => onReviewPage(row.index))}
              />
            ))}
          </View>
        )}

        {/* ── Actions ─────────────────────────────────────────────────────── */}
        <Actions
          onReplay={onReplay}
          onShare={onShare}
          onOpenClassReport={onOpenClassReport}
          onDone={onDone}
          accent={tok.accent}
        />
        {footer}
      </ScrollView>
    </View>
  )
}

// ── Verdict ───────────────────────────────────────────────────────────────────

/** The score, as a ring: the one number the learner actually came for. */
function ScoreRing({
  pct,
  graded,
  accent,
  track,
  ink,
  muted,
}: {
  pct: number
  /** Whether there was anything to score at all. */
  graded: boolean
  accent: string
  track: string
  ink: string
  muted: string
}) {
  const {t: l} = useLingui()
  const type = useReportType()
  const size = 128
  const stroke = 11
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  // With nothing scored there is no percentage to draw: the ring closes and the
  // centre says what the book *was*, instead of claiming a score of 100%.
  const filled = graded
    ? (Math.min(100, Math.max(0, pct)) / 100) * circumference
    : circumference

  return (
    <View
      testID="flashbook-report-ring"
      style={[styles.ring, {width: size, height: size}]}
      accessibilityRole="image"
      accessibilityLabel={
        graded ? l`${pct}% of the points` : l`Reading finished`
      }
      accessibilityHint={l`Your score, as a ring`}>
      <Svg width={size} height={size}>
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={track}
          strokeWidth={stroke}
          fill="none"
        />
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={accent}
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={`${filled} ${circumference}`}
          // The arc starts at twelve o'clock, which is where a reader's eye
          // already is, rather than at three.
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </Svg>
      <View style={styles.ringCenter} pointerEvents="none">
        {graded ? (
          <>
            <Text style={[type.display, {color: ink}]}>{pct}%</Text>
            <Text style={[type.caption, {color: muted}]}>
              <Trans>points</Trans>
            </Text>
          </>
        ) : (
          <Feather name="book-open" size={34} color={accent} />
        )}
      </View>
    </View>
  )
}

// ── Pieces ────────────────────────────────────────────────────────────────────

function StatTile({
  testID,
  icon,
  value,
  label,
  tone,
}: {
  testID: string
  icon: React.ComponentProps<typeof Feather>['name']
  value: string
  label: string
  /** Overrides the ink when the number is about success or loss. */
  tone?: string
}) {
  const type = useReportType()
  const pal = useFlashBookPlayPalette()
  const copy = useCopyStyle()
  return (
    <View
      testID={testID}
      style={[
        styles.tile,
        {backgroundColor: pal.surfaceAlt, borderColor: pal.paperHairline},
      ]}>
      <Feather name={icon} size={14} color={tone ?? pal.muted} />
      <Text style={[type.label, copy.text, {color: tone ?? pal.text}]}>
        {value}
      </Text>
      <Text
        style={[type.caption, copy.text, {color: pal.muted}]}
        numberOfLines={1}>
        {label}
      </Text>
    </View>
  )
}

/** One thing the numbers mean, with the tone the model gave it. */
function InsightRow({
  insight,
  positive,
  warning,
  accent,
}: {
  insight: ReportInsight
  positive: string
  warning: string
  accent: string
}) {
  const type = useReportType()
  const pal = useFlashBookPlayPalette()
  const copy = useCopyStyle()
  const colour =
    insight.tone === 'good'
      ? positive
      : insight.tone === 'warn'
        ? warning
        : accent
  const icon: React.ComponentProps<typeof Feather>['name'] =
    insight.kind === 'strongest'
      ? 'trending-up'
      : insight.kind === 'streak'
        ? 'award'
        : insight.kind === 'weakest'
          ? 'alert-triangle'
          : insight.kind === 'hardestPage' || insight.kind === 'skipped'
            ? 'alert-circle'
            : insight.kind === 'readings'
              ? 'headphones'
              : 'clock'

  return (
    <View
      testID={`flashbook-report-insight--${insight.kind}`}
      style={[
        styles.insight,
        {borderColor: pal.paperHairline, backgroundColor: pal.surfaceAlt},
      ]}>
      <Feather name={icon} size={15} color={colour} />
      <InsightSentenceText
        insight={insight}
        style={[type.body, copy.text, styles.flex, {color: pal.body}]}
      />
    </View>
  )
}

/**
 * An insight in words.
 *
 * The counts go through the translation layer as numbers rather than being baked
 * into a sentence, so a language that needs a different plural — or a different
 * word order — can say this properly. The prompt a `pace` row quotes is the
 * creator's own writing and is passed through untouched.
 */
function InsightSentenceText({
  insight,
  style,
}: {
  insight: ReportInsight
  style: StyleProp<TextStyle>
}) {
  const sentence = (() => {
    switch (insight.kind) {
      case 'strongest': {
        const {skill} = insight
        return (
          <Trans>
            Strongest area: {skill.label} — {skill.correct} of {skill.exercises}{' '}
            right.
          </Trans>
        )
      }
      case 'weakest': {
        const {skill} = insight
        return (
          <Trans>
            Worth another look: {skill.label} — {skill.wrong} of{' '}
            {skill.exercises} missed.
          </Trans>
        )
      }
      case 'hardestPage':
        return (
          <Trans>
            Page {insight.page.index + 1} cost you the most —{' '}
            {insight.page.mastery}% of its points.
          </Trans>
        )
      case 'skipped':
        return (
          <Trans>
            {insight.count} questions were never answered. Nothing wrong with
            coming back to them.
          </Trans>
        )
      case 'streak':
        return <Trans>{insight.count} correct in a row.</Trans>
      case 'pace':
        return insight.slowest ? (
          <Trans>
            About {Math.round(insight.msPerExercise / 1000)}s a question; you
            spent longest on “{truncate(insight.slowest.prompt)}”.
          </Trans>
        ) : (
          <Trans>
            About {Math.round(insight.msPerExercise / 1000)}s a question.
          </Trans>
        )
      case 'readings':
        return (
          <Trans>
            {insight.heard} of {insight.total} recordings played.
          </Trans>
        )
      default:
        return null
    }
  })()
  return <Text style={style}>{sentence}</Text>
}

/** One skill, as a bar and a fraction. */
function SkillBar({skill, accent}: {skill: SkillRow; accent: string}) {
  const type = useReportType()
  const pal = useFlashBookPlayPalette()
  const copy = useCopyStyle()
  const pct = Math.round(skill.accuracy * 100)
  const colour =
    skill.band === 'unscored'
      ? pal.muted
      : skill.band === 'perfect'
        ? pal.positive
        : skill.accuracy >= 0.5
          ? pal.warning
          : pal.negative

  return (
    <View
      testID={`flashbook-report-skill--${skill.type}`}
      style={styles.skillRow}>
      <View style={[styles.skillHead, copy.row]}>
        <Text
          style={[type.label, copy.text, styles.flex, {color: pal.text}]}
          numberOfLines={1}>
          {skill.label}
        </Text>
        <Text style={[type.caption, copy.text, {color: pal.muted}]}>
          {skill.band === 'unscored'
            ? `${skill.correct}/${skill.exercises}`
            : `${skill.correct}/${skill.answered} · ${pct}%`}
        </Text>
      </View>
      <View style={[styles.bar, {backgroundColor: pal.paperTrack}]}>
        <View
          style={[
            styles.barFill,
            {
              // An unseen skill draws no bar at all: a zero-width fill and a
              // zero-length fill are different claims, and only one is true.
              backgroundColor: skill.band === 'unscored' ? accent : colour,
              width: `${skill.answered > 0 ? pct : 0}%`,
            },
          ]}
        />
      </View>
    </View>
  )
}

/**
 * A question to look at again, with both answers side by side.
 *
 * The whole card is the way back to the page — a report that tells a learner what
 * to fix and then leaves them to find it in a hundred-page book has done half the
 * job.
 */
function FocusCard({
  entry,
  totalPages,
  positive,
  negative,
  warning,
  accent,
  onOpen,
}: {
  entry: FocusEntry
  totalPages: number
  positive: string
  negative: string
  warning: string
  accent: string
  onOpen?: () => void
}) {
  const {t: l} = useLingui()
  const type = useReportType()
  const pal = useFlashBookPlayPalette()
  const copy = useCopyStyle()
  const mark = entry.skipped || entry.partial ? warning : negative

  const card = (
    <View
      style={[
        styles.focus,
        {borderColor: pal.paperHairline, backgroundColor: pal.surfaceAlt},
      ]}>
      <View style={[styles.focusHead, copy.row]}>
        <View
          style={[
            styles.chip,
            {borderColor: mark + '66', backgroundColor: mark + '1f'},
          ]}>
          <Feather
            name={entry.skipped ? 'skip-forward' : 'x'}
            size={11}
            color={mark}
          />
          <Text style={[type.caption, copy.text, {color: mark}]}>
            {entry.skipped
              ? l`Not answered`
              : entry.partial
                ? l`Partly right`
                : l`Missed`}
          </Text>
        </View>
        <Text
          // The page's own title is the creator's writing, so it takes the page's
          // direction — and falls back to the report's when there is no title and
          // the label is the report's own "Page 2/4".
          style={[
            type.caption,
            entry.pageTitle.trim()
              ? directionTextStyle(entry.pageDirection)
              : copy.text,
            styles.flex,
            {color: pal.muted},
          ]}
          numberOfLines={1}>
          {entry.pageTitle.trim() ||
            l`Page ${entry.pageIndex + 1}/${totalPages}`}
        </Text>
      </View>

      {!!entry.prompt && (
        <Text
          // The question itself: the creator's writing, laid out its own way.
          style={[
            type.question,
            directionTextStyle(entry.direction),
            {color: pal.text},
          ]}
          numberOfLines={4}>
          {entry.prompt}
        </Text>
      )}

      <View style={styles.answers}>
        <AnswerLine
          label={l`Your answer`}
          value={entry.userAnswer}
          colour={mark}
          direction={entry.direction}
        />
        <AnswerLine
          label={l`Correct`}
          value={entry.correctAnswer}
          colour={positive}
          direction={entry.direction}
        />
      </View>

      {!!entry.explanation && (
        <Text
          style={[
            type.caption,
            directionTextStyle(entry.direction),
            {color: pal.body},
          ]}
          numberOfLines={4}>
          {entry.explanation}
        </Text>
      )}

      {onOpen && (
        <View style={[styles.focusFoot, copy.row]}>
          <Text style={[type.caption, copy.text, {color: accent}]}>
            <Trans>Open this page</Trans>
          </Text>
          <Feather
            name="arrow-right"
            size={13}
            color={accent}
            // The arrow points the way the reader reads, so a right-to-left book
            // gets the mirrored glyph rather than one pointing back at the text.
            style={
              copy.text.writingDirection === 'rtl' ? styles.flip : undefined
            }
          />
        </View>
      )}
    </View>
  )

  if (!onOpen) return card
  return (
    <TouchableOpacity
      testID={`flashbook-report-focus--${entry.blockId}`}
      accessibilityRole="button"
      accessibilityHint={l`Goes back to this question in the book`}
      accessibilityLabel={`${entry.label}: ${entry.prompt || entry.userAnswer}`}
      activeOpacity={0.85}
      onPress={onOpen}>
      {card}
    </TouchableOpacity>
  )
}

function AnswerLine({
  label,
  value,
  colour,
  direction,
}: {
  /** The report's own label — "Your answer", "Correct". */
  label: string
  /** What was written: the learner's or the creator's, in their own direction. */
  value: string
  colour: string
  direction: FlashBookDirection
}) {
  const type = useReportType()
  const pal = useFlashBookPlayPalette()
  const copy = useCopyStyle()
  return (
    <View style={[styles.answerLine, copy.row]}>
      <View style={[styles.answerDot, {backgroundColor: colour}]} />
      <Text style={[type.caption, copy.text, {color: pal.muted}]}>{label}</Text>
      <Text
        style={[
          type.label,
          directionTextStyle(direction),
          styles.flex,
          {color: pal.text},
        ]}
        numberOfLines={2}>
        {value}
      </Text>
    </View>
  )
}

/** One page of the journey, with its mastery and the way back into it. */
function JourneyRow({
  row,
  accent,
  onOpen,
}: {
  row: PageRow
  accent: string
  onOpen?: () => void
}) {
  const {t: l} = useLingui()
  const type = useReportType()
  const pal = useFlashBookPlayPalette()
  const copy = useCopyStyle()
  const colour =
    row.mastery === 100
      ? pal.positive
      : row.mastery >= 50
        ? accent
        : pal.warning

  const inner = (
    <View style={[styles.journey, copy.row]}>
      <View
        style={[
          styles.pageBadge,
          {
            borderColor: row.needsWork ? pal.warning + '66' : pal.paperHairline,
            backgroundColor: row.needsWork ? pal.warning + '1f' : 'transparent',
          },
        ]}>
        <Text
          style={[
            type.caption,
            {color: row.needsWork ? pal.warning : pal.muted},
          ]}>
          {row.index + 1}
        </Text>
      </View>
      <View style={styles.flex}>
        <Text
          testID={`flashbook-report-page-title--${row.index}`}
          // The page's own title when it has one — the creator's writing, in the
          // page's direction — and the report's own "Page 2" when it does not.
          style={[
            type.label,
            row.title ? directionTextStyle(row.direction) : copy.text,
            {color: pal.text},
          ]}
          numberOfLines={1}>
          {row.title || l`Page ${row.index + 1}`}
        </Text>
        <Text style={[type.caption, copy.text, {color: pal.muted}]}>
          {row.exercises > 0
            ? l`${row.correct}/${row.exercises} right · ${formatDuration(row.timeSpentMs)}`
            : l`${row.blocks} blocks · ${formatDuration(row.timeSpentMs)}`}
        </Text>
        {row.exercises > 0 && (
          <View
            style={[
              styles.bar,
              styles.barThin,
              {backgroundColor: pal.paperTrack},
            ]}>
            <View
              style={[
                styles.barFill,
                {backgroundColor: colour, width: `${row.mastery}%`},
              ]}
            />
          </View>
        )}
      </View>
      {row.exercises > 0 && (
        <Text style={[type.label, copy.text, {color: colour}]}>
          {row.mastery}%
        </Text>
      )}
      {onOpen && <Feather name="chevron-right" size={15} color={pal.muted} />}
    </View>
  )

  if (!onOpen) {
    return <View testID={`flashbook-report-page--${row.index}`}>{inner}</View>
  }
  return (
    <TouchableOpacity
      testID={`flashbook-report-page--${row.index}`}
      accessibilityRole="button"
      accessibilityHint={l`Opens this page of the book`}
      accessibilityLabel={row.title || l`Page ${row.index + 1}`}
      activeOpacity={0.85}
      onPress={onOpen}>
      {inner}
    </TouchableOpacity>
  )
}

/**
 * The whole book at a glance: one bar per page, in book order.
 *
 * A hundred pages of results cannot be *read*, but they can be *seen* — this is
 * the shape of a read: where it went well, where it got hard, and where it
 * stopped. The bars are pressable, so the map is also the way in.
 *
 * It is the report's own chart rather than the book's page furniture, so it runs
 * in the report's own direction: a reader of an English report scanning a map of
 * page twenty knows which end page one is at without thinking about it.
 */
function MasteryStrip({
  pages,
  accent,
  onOpen,
}: {
  pages: PageRow[]
  accent: string
  onOpen?: (pageIndex: number) => void
}) {
  const type = useReportType()
  const pal = useFlashBookPlayPalette()
  const copy = useCopyStyle()
  const {t: l} = useLingui()
  return (
    <View testID="flashbook-report-strip" style={styles.stripWrap}>
      <View style={styles.strip}>
        {pages.map(row => {
          const colour =
            row.exercises === 0
              ? pal.paperTrack
              : row.answered === 0
                ? pal.muted + '55'
                : row.mastery === 100
                  ? pal.positive
                  : row.mastery >= 50
                    ? accent
                    : pal.warning
          const unread = row.exercises > 0 && row.answered === 0
          return (
            <TouchableOpacity
              key={row.index}
              disabled={!onOpen}
              accessibilityRole="button"
              accessibilityLabel={l`Page ${row.index + 1} — ${row.mastery}%`}
              accessibilityHint={l`Opens this page of the book`}
              onPress={() => onOpen?.(row.index)}
              style={[
                styles.stripBar,
                {
                  backgroundColor: colour,
                  // A page that was never answered is drawn flat and short, so
                  // the shape of *what was read* is visible at a glance.
                  height: unread ? 7 : 22,
                },
              ]}
            />
          )
        })}
      </View>
      <Text style={[type.caption, copy.text, {color: pal.muted}]}>
        <Trans>Each bar is a page. Press one to open it.</Trans>
      </Text>
    </View>
  )
}

function Actions({
  onReplay,
  onShare,
  onOpenClassReport,
  onDone,
  accent,
}: {
  onReplay?: () => void
  onShare?: () => void
  onOpenClassReport?: () => void
  onDone: () => void
  accent: string
}) {
  const {t: l} = useLingui()
  const type = useReportType()
  const pal = useFlashBookPlayPalette()
  const copy = useCopyStyle()
  // A book's own action shape, from the theme's answer langauge: on ruled paper
  // the action stops being a filled pill and becomes the underlined instruction a
  // workbook prints at the end of an exercise. The app's pill here is what makes a
  // report look like a form.
  const action = usePlayPrimaryAction(48)

  return (
    <View style={[styles.actions, copy.row]}>
      {/*
        The teacher's door, and only on their own book.

        It sits *above* the learner's own actions and says what is inside rather
        than "class report": a teacher reading `5 of 9 missed the fraction question`
        needs to know that is where they are going, and a button that promises the
        class's answers before the class has answered is what the empty state is
        for.
      */}
      {onOpenClassReport && (
        <TouchableOpacity
          testID="flashbook-report-class"
          accessibilityRole="button"
          accessibilityHint={l`Opens the class's results for this book`}
          onPress={onOpenClassReport}
          style={[
            styles.actionBtn,
            {
              borderColor: pal.paperHairline,
              backgroundColor: pal.surfaceAlt,
              borderRadius: 14,
            },
          ]}>
          <Feather name="users" size={15} color={accent} />
          <Text style={[type.label, copy.text, {color: accent}]}>
            <Trans>See how the class did</Trans>
          </Text>
        </TouchableOpacity>
      )}
      {/*
        Telling somebody, which sits with the learner's own actions rather than
        with the teacher's door above: a report is worth showing, and the picture
        is the half of it that travels.
      */}
      {onShare && (
        <TouchableOpacity
          testID="flashbook-report-share-btn"
          accessibilityRole="button"
          accessibilityHint={l`Opens the card you can post or save`}
          onPress={onShare}
          style={[
            styles.actionBtn,
            {
              borderColor: pal.paperHairline,
              backgroundColor: pal.surfaceAlt,
              borderRadius: 14,
            },
          ]}>
          <Feather name="share-2" size={15} color={accent} />
          <Text style={[type.label, copy.text, {color: accent}]}>
            <Trans>Share my report</Trans>
          </Text>
        </TouchableOpacity>
      )}
      {onReplay && (
        <TouchableOpacity
          testID="flashbook-report-replay"
          accessibilityRole="button"
          accessibilityHint={l`Starts the book again from its cover`}
          onPress={onReplay}
          style={[
            styles.actionBtn,
            {
              borderColor: pal.paperHairline,
              backgroundColor: pal.surfaceAlt,
              borderRadius: 14,
            },
          ]}>
          <Feather name="refresh-cw" size={15} color={accent} />
          <Text style={[type.label, copy.text, {color: accent}]}>
            <Trans>Read again</Trans>
          </Text>
        </TouchableOpacity>
      )}
      <TouchableOpacity
        testID="flashbook-report-done"
        accessibilityRole="button"
        onPress={onDone}
        style={[
          styles.actionBtn,
          {backgroundColor: accent, borderRadius: 14},
          action.style,
        ]}>
        <Text
          style={[
            type.label,
            copy.text,
            {color: action.ink(true) ?? pal.onAccent},
            action.labelStyle(true),
          ]}>
          <Trans>Done</Trans>
        </Text>
      </TouchableOpacity>
    </View>
  )
}

function SectionTitleText({children}: {children: ReactNode}) {
  const type = useReportType()
  const pal = useFlashBookPlayPalette()
  const copy = useCopyStyle()
  return (
    <Text
      style={[type.title, copy.text, {color: pal.text}, styles.sectionTitle]}>
      {children}
    </Text>
  )
}

// ── Small shared helpers ──────────────────────────────────────────────────────

/**
 * The book's own type, for every role the report prints in.
 *
 * Read here rather than per text node because it follows the *book*: a workbook's
 * report is set in the workbook's hand, an editorial theme's in its serif. Reading
 * it is what keeps the last page from being the one screen in the app's UI font.
 */
/**
 * Which way the report's *own* words run, and the styles that carry it.
 *
 * The report is a mixed surface. Its verdict, its insights, its labels and its
 * numbers are app copy — they are read from the translation catalogue, so they are
 * written in the reader's language whatever language the book is in. The book's
 * words are not: a prompt, an answer and a page title are the creator's and the
 * learner's, and they carry their own direction (see `lib/flashbookReport`).
 *
 * Setting only one of the two is what produces the bug this exists for: an English
 * sentence inside a right-to-left report lays out with its full stop at the wrong
 * end and its numbers reversed, so "3 of 6 correct." prints as ".of 6 correct 3".
 */

/** A question's own words, short enough to sit inside a sentence. */
function truncate(text: string, max = 42): string {
  const clean = text.replace(/\s+/g, ' ').trim()
  return clean.length <= max ? clean : `${clean.slice(0, max - 1)}…`
}

const styles = StyleSheet.create({
  root: {flex: 1},
  scroll: {paddingTop: 22, paddingBottom: 44},
  flex: {flex: 1},
  flip: {transform: [{scaleX: -1}]},

  hero: {alignItems: 'center', gap: 8},
  /** The "# reads of this book" line, quieter than the verdict it follows. */
  readCount: {flexDirection: 'row', alignItems: 'center', gap: 5},
  readCountIcon: {marginTop: 1},
  ring: {alignItems: 'center', justifyContent: 'center'},
  ringCenter: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },

  tiles: {flexDirection: 'row', flexWrap: 'wrap', gap: 8},
  tile: {
    flexGrow: 1,
    flexBasis: 96,
    borderWidth: 1,
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    gap: 2,
  },

  insights: {gap: 8},
  insight: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 9,
    borderWidth: 1,
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },

  section: {gap: 10},
  practiceBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: 16,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  practiceBtnText: {flex: 1, gap: 1},
  sectionTitle: {marginTop: 4},

  skillRow: {gap: 5},
  skillHead: {flexDirection: 'row', alignItems: 'center', gap: 8},
  bar: {height: 7, borderRadius: 4, overflow: 'hidden'},
  barThin: {height: 4, marginTop: 5},
  barFill: {height: '100%', borderRadius: 4},

  empty: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
  },

  focus: {borderWidth: 1, borderRadius: 12, padding: 13, gap: 8},
  focusHead: {flexDirection: 'row', alignItems: 'center', gap: 8},
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  answers: {gap: 4},
  answerLine: {flexDirection: 'row', alignItems: 'center', gap: 7},
  answerDot: {width: 6, height: 6, borderRadius: 3},
  focusFoot: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 5,
  },

  journey: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 7,
  },
  pageBadge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },

  stripWrap: {gap: 6},
  strip: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 3,
    flexWrap: 'wrap',
    minHeight: 24,
  },
  stripBar: {width: 7, borderRadius: 3},

  actions: {flexDirection: 'row', gap: 10, marginTop: 10},
  actionBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    minHeight: 48,
    borderWidth: 1,
    borderColor: 'transparent',
  },
})
